/**
 * PATH: packages/email-hub/src/feature/reader-header/runtime/errorClassification.ts
 * FILE_ID: EMAILHUB-READERHEADER-RUNTIME-ERRORS-001
 * PURPOSE: Map transport failures (HTTP status / network / abort) onto the
 *   shared ReaderErrorClass vocabulary so every toast reads correctly
 *   (403 = permission, never "Could not …").
 * ROLE IN ARCHITECTURE: Pure classification leaf under the action runner.
 * MAIN EXPORTS: classifyReaderError, readerErrorStatus.
 * NON-RESPONSIBILITIES: No copy — descriptor `errors` mappings own the words.
 * NOTES FOR FUTURE AI: Transports differ (axios-like `.response.status`,
 *   fetch wrappers with `.status`, DRF `detail` bodies). Extend the status
 *   probes here, never at call sites.
 */
import type { ReaderErrorClass } from '../model/types'

/** Best-effort HTTP status extraction across axios/fetch/custom errors. */
export function readerErrorStatus(error: unknown): number | null {
  if (!error || typeof error !== 'object') return null
  const err = error as {
    status?: unknown
    statusCode?: unknown
    response?: { status?: unknown }
  }
  for (const candidate of [err.status, err.statusCode, err.response?.status]) {
    if (typeof candidate === 'number' && candidate >= 100) return candidate
  }
  return null
}

/**
 * Classify one thrown transport error.
 *
 * Inputs: anything thrown by a mutation transport.
 * Output: one ReaderErrorClass; unknown shapes default to 'server' (loud,
 * honest "nothing changed" copy) rather than pretending it was the network.
 * Side effects / failure modes: none — pure.
 */
export function classifyReaderError(error: unknown): ReaderErrorClass {
  const status = readerErrorStatus(error)
  if (status !== null) {
    if (status === 401 || status === 403) return 'perm'
    if (status === 404 || status === 410) return 'notfound'
    if (status === 409 || status === 412) return 'conflict'
    if (status === 400 || status === 422) return 'validation'
    if (status >= 500) return 'server'
    if (status >= 400) return 'validation'
    return 'server'
  }
  if (error instanceof DOMException && error.name === 'AbortError') {
    return 'network'
  }
  const name = (error as { name?: string } | null)?.name
  if (name === 'AbortError' || name === 'TimeoutError') return 'network'
  // fetch() network failures surface as TypeError('Failed to fetch').
  if (error instanceof TypeError) return 'network'
  return 'server'
}
